import React, { useEffect, useState } from 'react';
import styles from './GiveKudos.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  setHeaderConfigsAction,
  setHeaderTitleAction
} from '../../../reactRedux/Actions/headerAction';
import { useHistory } from 'react-router-dom';
import AddCircleOutlineRoundedIcon from '@material-ui/icons/AddCircleOutlineRounded';
import CachedRoundedIcon from '@material-ui/icons/CachedRounded';
import Card from '../../common/cards/Card';
import CardSlider from '../../common/cards/CardSlider';
import { Coin } from '../../../assets/icons/iconComponents2';
import {
  addContentAction,
  addFabAction,
  closeSliderDialogAction,
  openSliderDialogAction
} from '../../../reactRedux/Actions/showSliderDialogAction';
import UserSearchDialog from '../../_dialogContent/UserSearchDialog';
import classNames from 'classnames';
import Loader from '../../common/Loader';
import { routes } from '../../../assets/routes';
import { toast } from 'react-toastify';
import Avatar from '@material-ui/core/Avatar';
import { Helmet } from 'react-helmet';
import useDesktopDisplay from '../../../hooks/useDesktopDisplay';
import { toggleBackButtonAction } from '../../../reactRedux/Actions/sideBarAction';
import zeroScoreIcon from '../../../assets/icons/zeroScoreIcon.jpg';
import AvatarReco from '../../common/AvatarReco';
import RoundedChip from '../../common/RoundedChip';
import IconButton from '@material-ui/core/IconButton';
import Note from '../../common/Note';
import { modules } from '../../../helpers/modules';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { sendKudosRequest } from './api';
import {
  getOrganizationFeaturesRequest,
  kudosCardsRequest
} from '../../../helpers/api';
import { TextField } from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from '../../../i18n/LanguageProvider';
import { formatNumber } from '../../../i18n';

const COIN_OPTIONS = [5, 10, 15, 20];

const GiveKudos = (props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isDesktop = useDesktopDisplay();

  const [card, setCard] = useState(null);
  const [cardDescription, setCardDescription] = useState(null);
  const [point, setPoint] = useState(false);
  const [users, setUsers] = useState(
    props.location.state || (modules.group_reco ? [] : {})
  );
  const [note, setNote] = useState('');
  const [placeHolder] = useState(() => {
    const sentences = t(
      users[0]?.birthday
        ? 'giveKudos.examples.birthday'
        : users[0]?.anniversary
        ? 'giveKudos.examples.anniversary'
        : 'giveKudos.examples.kudos'
    );
    return sentences[Math.floor(Math.random() * sentences.length)];
  });

  const setHeader = (title) => dispatch(setHeaderTitleAction(title));
  const setHeaderConfigs = (configs) =>
    dispatch(setHeaderConfigsAction(configs));
  const openSliderDialog = () => dispatch(openSliderDialogAction());
  const closeDialog = () => dispatch(closeSliderDialogAction());
  const setDialogContent = (content) => dispatch(addContentAction(content));
  const setDialogFab = (fab) => dispatch(addFabAction(fab));
  const setSideBarBackButton = (show) => dispatch(toggleBackButtonAction(show));

  const userCredit = useSelector((state) => state.userReducer.credit);

  const {
    isLoading,
    data,
    refetch: refetchCards
  } = useQuery(['kudosCards'], () => kudosCardsRequest(false), {
    initialData: [],
    select: React.useCallback((data) => data?.data, []),
    onError(err) {
      toast.error(err.response?.data?.detail || t('common.errorFormal'));
    }
  });

  const { data: orgFeatures } = useQuery(
    ['organizationFeatures'],
    getOrganizationFeaturesRequest
  );

  const isCoinCapActive = !!orgFeatures?.data?.find(
    (feature) => feature.feature_name === 'kudos_card_coin_cap'
  )?.active;

  const selectedCard = data?.find((c) => c.id === card);
  const maxCoin =
    isCoinCapActive && selectedCard?.max_coin != null
      ? selectedCard.max_coin
      : null;
  const allowedCoins =
    maxCoin == null ? COIN_OPTIONS : COIN_OPTIONS.filter((c) => c <= maxCoin);
  const isPointAllowed = !point || allowedCoins.includes(point);

  // when the selected card's cap drops below the chosen point, fall back to the largest allowed option
  useEffect(() => {
    if (!isPointAllowed) {
      setPoint(
        allowedCoins.length ? allowedCoins[allowedCoins.length - 1] : false
      );
    }
  }, [isPointAllowed]);

  const sendKudosMutation = useMutation(sendKudosRequest, {
    onSuccess() {
      toast.success(t('giveKudos.success'));
      history.push(routes.HOME);
    },
    onError(err) {
      toast.error(err.response?.data.detail || t('common.errorPlain'));
      if (err.response?.status === 400) refetchCards();
    }
  });

  useEffect(() => {
    setSideBarBackButton(true);

    return () => setSideBarBackButton(false);
  }, []);

  useEffect(() => {
    setHeader(t('giveKudos.title'));
    setHeaderConfigs({ credits: true, exit: false, back: true, logo: false });
  }, []);

  const selectUsers = (data) => {
    if (modules.group_reco) {
      setUsers(data);
    } else {
      return () => {
        setUsers(data);
        closeDialog();
      };
    }
  };

  const setDialog = () => {
    if (modules.group_reco) {
      setDialogContent(
        <UserSearchDialog
          multi
          selectUsers={selectUsers}
          currentUsers={users}
        />
      );
    } else {
      setDialogContent(<UserSearchDialog selectUsers={selectUsers} />);
    }
    setDialogFab([
      {
        type: modules.group_reco ? 'accept' : 'default',
        title: modules.group_reco ? t('giveKudos.selected') : '',
        isClose: false,
        iconOnly: !modules.group_reco,
        onClickFn: closeDialog
      }
    ]);
    openSliderDialog();
  };

  const choosePoint = (p) => () => {
    setPoint(p);
  };

  const handleNote = (e) => {
    setNote(e.target.value);
  };

  const handleDeleteUser = (u) => {
    setUsers(users.filter((item) => item.id !== u.id));
  };

  const generateAttentionText = () => {
    let text;
    if (point * users.length <= userCredit) {
      text = t('giveKudos.groupCost', {
        point,
        count: users.length,
        total: point * users.length
      });
    } else {
      text = t('giveKudos.groupShortage', {
        point,
        count: users.length,
        missing: point * users.length - userCredit
      });
    }

    return text;
  };

  return (
    <div className={classNames(styles.page, styles.full)}>
      <Helmet>
        <title>{t('giveKudos.title')}</title>
      </Helmet>
      {isDesktop && (
        <div className={styles.pageHeader}>{t('giveKudos.title')}</div>
      )}
      <div className={styles.section}>
        {isDesktop && (
          <div className={styles.sectionTop}>
            <h4>{t('giveKudos.whoQuestion')}</h4>
          </div>
        )}
        <div className={styles.sectionBody}>
          {!modules.group_reco && (
            <span className={styles.select2} onClick={setDialog}>
              {Object.keys(users).length ? (
                <>
                  <Avatar
                    alt={users.name}
                    src={users.img}
                    className={styles.small}
                  />
                  <span>{users.name}</span>
                  <CachedRoundedIcon />
                </>
              ) : (
                <>
                  <AddCircleOutlineRoundedIcon />
                  <span>{t('giveKudos.selectColleague')}</span>
                </>
              )}
            </span>
          )}

          {modules.group_reco && (
            <span
              className={styles.select}
              onClick={!Object.keys(users).length ? setDialog : null}
            >
              {Object.keys(users).length ? (
                <div className={classNames(styles.users, styles.selfCenter)}>
                  {users.map((u, i) => (
                    <RoundedChip
                      key={i}
                      name={u.name}
                      onDelete={() => handleDeleteUser(u)}
                      avatar={u.img}
                    />
                  ))}
                </div>
              ) : (
                <>
                  <span className={styles.selfCenter}>
                    {t('giveKudos.whichColleague')}
                  </span>
                </>
              )}
              <IconButton
                size="small"
                onClick={setDialog}
                className={styles.addBtn}
              >
                <AddCircleOutlineRoundedIcon />
              </IconButton>
            </span>
          )}
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.sectionTop}>
          <h4>{t('giveKudos.forWhat')}</h4>
        </div>
        <div className={styles.sectionBody}>
          <TextField
            className={styles.note}
            id="give-kudos"
            name="give-kudos"
            multiline
            rows={5}
            hiddenLabel
            value={note}
            onChange={handleNote}
            placeholder={placeHolder}
          />
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.sectionTop}>
          <h4>{t('giveKudos.howMany')}</h4>
        </div>
        <div className={styles.sectionBody}>
          <div className={styles.pointWrapper}>
            <Card
              extraClass={styles.point}
              selected={point === 0}
              onClick={choosePoint(0)}
            >
              <AvatarReco size={4} avatar={zeroScoreIcon} />
            </Card>
            {allowedCoins.map((c) => (
              <Card
                key={c}
                extraClass={styles.point}
                selected={point === c}
                onClick={choosePoint(c)}
                disabled={userCredit < c}
              >
                <span>{c}</span>
                <Coin />
              </Card>
            ))}
          </div>
          {!allowedCoins.length && (
            <Note
              type="default"
              text={t('giveKudos.maxCoin', { max: formatNumber(maxCoin) })}
            />
          )}
          {modules.group_reco && users.length > 1 && point > 0 && (
            <Note
              type={point * users.length > userCredit ? 'danger' : 'default'}
              text={generateAttentionText()}
            />
          )}
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.sectionTop}>
          <h4>{t('giveKudos.whichCard')}</h4>
        </div>
        <CardSlider>
          {isLoading ? (
            <Loader size={40} />
          ) : (
            data?.map((c, i) => (
              <div key={i} title={c.description}>
                <Card
                  selected={card === c.id}
                  extraClass={styles.card}
                  onClick={() => {
                    setCard(c.id);
                    setCardDescription(c.description);
                  }}
                  image={c.photo?.absolute_url}
                >
                  {c.title}
                </Card>
              </div>
            ))
          )}
        </CardSlider>
      </div>
      {cardDescription && <Note type="default" text={cardDescription} />}

      <div className={styles.btnWrapper}>
        <Button
          variant="contained"
          disabled={
            (modules.group_reco ? !users.length : !users.id) ||
            !card ||
            sendKudosMutation.isLoading ||
            (modules.group_reco && point * users.length > userCredit) ||
            point === false ||
            !isPointAllowed ||
            !note.length
          }
          loading={sendKudosMutation.isLoading}
          onClick={() =>
            sendKudosMutation.mutate({
              receiverId: users?.id,
              receiverIds: modules.group_reco ? users?.map((u) => u.id) : [],
              cardId: card,
              coin: point,
              note
            })
          }
          color="primary"
          fullWidth
        >
          {t('giveKudos.send')}
        </Button>
        <Button fullWidth variant="outlined" onClick={history.goBack}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
};

GiveKudos.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    state: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string
    ])
  })
};

export default GiveKudos;
